import React, { useMemo } from "react";
import {
  Box,
  Grid,
  Paper,
  Typography,
  Stack,
  Chip
} from "@mui/material";
import {
  ResponsiveContainer,
  BarChart,
  Bar,
  XAxis,
  YAxis,
  Tooltip,
  CartesianGrid,
  PieChart,
  Pie,
  Cell,
  LineChart,
  Line
} from "recharts";
import TrendingUpOutlinedIcon from "@mui/icons-material/TrendingUpOutlined";
import PaidOutlinedIcon from "@mui/icons-material/PaidOutlined";
import StorefrontOutlinedIcon from "@mui/icons-material/StorefrontOutlined";
import GroupsOutlinedIcon from "@mui/icons-material/GroupsOutlined";
import Inventory2OutlinedIcon from "@mui/icons-material/Inventory2Outlined";
import QueryStatsOutlinedIcon from "@mui/icons-material/QueryStatsOutlined";

const COLORS = ["#1565C0", "#2E7D32", "#ED6C02", "#7B1FA2", "#D32F2F", "#00838F", "#6D4C41", "#455A64"];

const money = (value) => `$${Number(value || 0).toFixed(2)}`;
const number = (value) => Number(value || 0).toFixed(2);

const ChartCard = ({ title, subtitle, icon, children, height = 320 }) => {
  return (
    <Paper
      sx={{
        borderRadius: 3,
        border: "1px solid #e2e8f0",
        overflow: "hidden",
        boxShadow: "0 10px 28px rgba(15,23,42,0.04)",
        height: "100%"
      }}
    >
      <Box
        sx={{
          p: 2.2,
          display: "flex",
          alignItems: "center",
          justifyContent: "space-between",
          gap: 2,
          borderBottom: "1px solid #e2e8f0",
          bgcolor: "#fff"
        }}
      >
        <Box>
          <Typography sx={{ fontWeight: 900, color: "#0f172a", fontSize: 17 }}>
            {title}
          </Typography>
          {subtitle && (
            <Typography sx={{ color: "#64748b", fontSize: 12.5, mt: 0.35 }}>
              {subtitle}
            </Typography>
          )}
        </Box>

        <Box
          sx={{
            width: 42,
            height: 42,
            borderRadius: 2.5,
            bgcolor: "#eaf3ff",
            color: "#1565C0",
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
            flexShrink: 0
          }}
        >
          {icon}
        </Box>
      </Box>

      <Box sx={{ p: 2, height }}>{children}</Box>
    </Paper>
  );
};

const StatCard = ({ title, value, helper, icon, color = "#1565C0" }) => {
  return (
    <Paper
      sx={{
        p: 2.4,
        borderRadius: 3,
        border: "1px solid #e2e8f0",
        boxShadow: "0 10px 28px rgba(15,23,42,0.04)",
        height: "100%"
      }}
    >
      <Box sx={{ display: "flex", justifyContent: "space-between", gap: 2 }}>
        <Box>
          <Typography sx={{ color: "#64748b", fontSize: 12.5, fontWeight: 800 }}>
            {title}
          </Typography>
          <Typography sx={{ color: "#0f172a", fontSize: 28, fontWeight: 950, mt: 0.6 }}>
            {value}
          </Typography>
          {helper && (
            <Typography sx={{ color: "#64748b", fontSize: 12, mt: 0.4 }}>
              {helper}
            </Typography>
          )}
        </Box>

        <Box
          sx={{
            width: 46,
            height: 46,
            borderRadius: 2.5,
            bgcolor: `${color}18`,
            color,
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
            flexShrink: 0
          }}
        >
          {icon}
        </Box>
      </Box>
    </Paper>
  );
};

const EmptyChart = ({ text = "Sin datos suficientes para graficar" }) => (
  <Box
    sx={{
      height: "100%",
      minHeight: 220,
      border: "1px dashed #cbd5e1",
      borderRadius: 3,
      display: "flex",
      alignItems: "center",
      justifyContent: "center",
      textAlign: "center",
      p: 3,
      color: "#64748b",
      fontWeight: 800
    }}
  >
    {text}
  </Box>
);

const CustomTooltip = ({ active, payload, label }) => {
  if (!active || !payload || !payload.length) return null;

  return (
    <Paper sx={{ p: 1.3, borderRadius: 2, border: "1px solid #e2e8f0" }}>
      <Typography sx={{ fontWeight: 800, fontSize: 12.5, color: "#0f172a", mb: 0.5 }}>
        {label || payload[0]?.name}
      </Typography>
      {payload.map((item, index) => (
        <Typography key={index} sx={{ fontSize: 12, color: item.color || "#0f172a" }}>
          {item.name}: {typeof item.value === "number" ? item.value.toFixed(2) : item.value}
        </Typography>
      ))}
    </Paper>
  );
};

const ProductChartsTab = ({ product, dashboardData }) => {
  const data = dashboardData || {};
  const stats = data.stats || {};
  const records = data.records || {};

  const providers = Array.isArray(stats.proveedores) ? stats.proveedores : [];
  const clients = Array.isArray(stats.clientes) ? stats.clientes : [];
  const purchaseHistory = Array.isArray(stats.historial_precios_compra) ? stats.historial_precios_compra : [];
  const saleHistory = Array.isArray(stats.historial_precios_venta) ? stats.historial_precios_venta : [];
  const purchases = Array.isArray(records.purchases) ? records.purchases : [];
  const sales = Array.isArray(records.sales) ? records.sales : [];

  const variants = useMemo(() => {
    if (Array.isArray(data.variants) && data.variants.length) return data.variants;
    if (Array.isArray(product?.variants)) return product.variants;
    return [];
  }, [data.variants, product?.variants]);

  const productStocks = useMemo(() => {
    return Array.isArray(product?.stocks_bodega) ? product.stocks_bodega : [];
  }, [product?.stocks_bodega]);

  const variantFactorMap = useMemo(() => {
    const map = new Map();

    variants.forEach((variant) => {
      const factor = Number(variant.factor_unidades || variant.conversion_quantity || 1) || 1;
      map.set(Number(variant.id), factor);
    });

    return map;
  }, [variants]);

  const stockByBodega = useMemo(() => {
    const dashboardRows = Array.isArray(data.stock_by_bodega) ? data.stock_by_bodega : [];

    if (dashboardRows.length) {
      return dashboardRows;
    }

    const grouped = new Map();

    productStocks.forEach((row) => {
      const key = String(row.bodega_id || row.bodega_codigo || row.bodega_nombre || "sin-bodega");
      const factor = row.variant_id ? variantFactorMap.get(Number(row.variant_id)) || 1 : 1;
      const cantidadEquivalente = Number(row.cantidad || 0) * factor;

      if (!grouped.has(key)) {
        grouped.set(key, {
          id: key,
          bodega_id: row.bodega_id,
          bodega_codigo: row.bodega_codigo,
          bodega_nombre: row.bodega_nombre,
          cantidad: 0,
          stock_minimo: row.stock_minimo ?? product?.stock_minimo ?? 0,
          activo: row.activo
        });
      }

      const current = grouped.get(key);
      current.cantidad += cantidadEquivalente;
    });

    return Array.from(grouped.values());
  }, [data.stock_by_bodega, productStocks, product?.stock_minimo, variantFactorMap]);

  const resumen = useMemo(() => {
    return {
      compraTotal: Number(stats.costo_total_compras || 0),
      ventaNeta: Number(stats.venta_total_neta || 0),
      utilidad: Number(stats.utilidad_bruta_estimada || 0),
      margen: Number(stats.margen_bruto_estimado_pct || 0),
      promedioCompra: Number(stats.precio_promedio_compra || 0),
      promedioVenta: Number(stats.precio_promedio_venta || 0),
      cantidadComprada: Number(stats.cantidad_comprada || 0),
      cantidadVendida: Number(stats.cantidad_vendida || 0),
      stock: Number(product?.stock ?? product?.stock_real ?? stats.stock_total ?? 0)
    };
  }, [stats, product]);

  const rentabilidadData = [
    { name: "Compras", value: resumen.compraTotal },
    { name: "Ventas", value: resumen.ventaNeta },
    { name: "Utilidad", value: resumen.utilidad }
  ];

  const preciosData = [
    { name: "Compra prom.", value: resumen.promedioCompra },
    { name: "Venta prom.", value: resumen.promedioVenta },
    { name: "PVP actual", value: Number(product?.pvp1 || stats.precio_actual || 0) }
  ];

  const proveedoresData = providers.slice(0, 8).map((p) => ({
    name: p.proveedor || "Proveedor",
    total: Number(p.total || 0),
    cantidad: Number(p.cantidad || 0),
    promedio: Number(p.precio_promedio || 0)
  }));

  const clientesData = clients.slice(0, 8).map((c) => ({
    name: c.cliente || "Cliente",
    total: Number(c.total || 0),
    cantidad: Number(c.cantidad || 0),
    promedio: Number(c.precio_promedio || 0)
  }));

  const comprasLinea = purchaseHistory.map((item) => ({
    fecha: item.fecha || "",
    precio: Number(item.precio_promedio || 0),
    cantidad: Number(item.cantidad || 0),
    total: Number(item.total || 0)
  }));

  const ventasLinea = saleHistory.map((item) => ({
    fecha: item.fecha || "",
    precio: Number(item.precio_promedio || 0),
    cantidad: Number(item.cantidad || 0),
    total: Number(item.total || 0)
  }));

  const stockData = stockByBodega.map((s) => ({
    name: s.bodega_nombre || s.bodega_codigo || "Bodega",
    cantidad: Number(s.cantidad || 0),
    minimo: Number(s.stock_minimo || product?.stock_minimo || 0)
  }));

  const actividadData = [
    { name: "Compras", value: purchases.length },
    { name: "Ventas", value: sales.length },
    { name: "Proveedores", value: providers.length },
    { name: "Clientes", value: clients.length }
  ].filter((item) => item.value > 0);

  return (
    <Box>
      <Grid container spacing={2.5} sx={{ mb: 2.5 }}>
        <Grid item xs={12} md={3}>
          <StatCard
            title="Venta neta"
            value={money(resumen.ventaNeta)}
            helper={`${number(resumen.cantidadVendida)} unidades vendidas`}
            icon={<PaidOutlinedIcon />}
            color="#1565C0"
          />
        </Grid>

        <Grid item xs={12} md={3}>
          <StatCard
            title="Costo compras"
            value={money(resumen.compraTotal)}
            helper={`${number(resumen.cantidadComprada)} unidades compradas`}
            icon={<StorefrontOutlinedIcon />}
            color="#ED6C02"
          />
        </Grid>

        <Grid item xs={12} md={3}>
          <StatCard
            title="Utilidad estimada"
            value={money(resumen.utilidad)}
            helper={`Margen estimado: ${number(resumen.margen)}%`}
            icon={<TrendingUpOutlinedIcon />}
            color="#2E7D32"
          />
        </Grid>

        <Grid item xs={12} md={3}>
          <StatCard
            title="Stock actual"
            value={number(resumen.stock)}
            helper={`Stock mínimo: ${number(product?.stock_minimo || stats.stock_minimo || 0)}`}
            icon={<Inventory2OutlinedIcon />}
            color="#7B1FA2"
          />
        </Grid>
      </Grid>

      <Grid container spacing={2.5}>
        <Grid item xs={12} md={6}>
          <ChartCard
            title="Rentabilidad general"
            subtitle="Compras, ventas y utilidad estimada"
            icon={<QueryStatsOutlinedIcon />}
          >
            {rentabilidadData.some((i) => i.value > 0) ? (
              <ResponsiveContainer width="100%" height="100%">
                <BarChart data={rentabilidadData}>
                  <CartesianGrid strokeDasharray="3 3" vertical={false} />
                  <XAxis dataKey="name" tick={{ fontSize: 12 }} />
                  <YAxis tick={{ fontSize: 12 }} />
                  <Tooltip content={<CustomTooltip />} />
                  <Bar dataKey="value" name="Valor" radius={[10, 10, 0, 0]}>
                    {rentabilidadData.map((_entry, index) => (
                      <Cell key={index} fill={COLORS[index % COLORS.length]} />
                    ))}
                  </Bar>
                </BarChart>
              </ResponsiveContainer>
            ) : (
              <EmptyChart />
            )}
          </ChartCard>
        </Grid>

        <Grid item xs={12} md={6}>
          <ChartCard
            title="Comparativo de precios"
            subtitle="Precio promedio de compra, venta y PVP actual"
            icon={<PaidOutlinedIcon />}
          >
            {preciosData.some((i) => i.value > 0) ? (
              <ResponsiveContainer width="100%" height="100%">
                <BarChart data={preciosData}>
                  <CartesianGrid strokeDasharray="3 3" vertical={false} />
                  <XAxis dataKey="name" tick={{ fontSize: 12 }} />
                  <YAxis tick={{ fontSize: 12 }} />
                  <Tooltip content={<CustomTooltip />} />
                  <Bar dataKey="value" name="Precio" radius={[10, 10, 0, 0]}>
                    {preciosData.map((_entry, index) => (
                      <Cell key={index} fill={COLORS[index % COLORS.length]} />
                    ))}
                  </Bar>
                </BarChart>
              </ResponsiveContainer>
            ) : (
              <EmptyChart />
            )}
          </ChartCard>
        </Grid>

        <Grid item xs={12} md={6}>
          <ChartCard
            title="Proveedores principales"
            subtitle="Top proveedores por valor comprado"
            icon={<StorefrontOutlinedIcon />}
          >
            {proveedoresData.length > 0 ? (
              <ResponsiveContainer width="100%" height="100%">
                <PieChart>
                  <Pie
                    data={proveedoresData}
                    dataKey="total"
                    nameKey="name"
                    innerRadius={58}
                    outerRadius={105}
                    paddingAngle={3}
                    label={({ name, percent }) => `${String(name).slice(0, 12)} ${((percent || 0) * 100).toFixed(0)}%`}
                  >
                    {proveedoresData.map((_entry, index) => (
                      <Cell key={index} fill={COLORS[index % COLORS.length]} />
                    ))}
                  </Pie>
                  <Tooltip content={<CustomTooltip />} />
                </PieChart>
              </ResponsiveContainer>
            ) : (
              <EmptyChart text="Sin compras suficientes para mostrar proveedores" />
            )}
          </ChartCard>
        </Grid>

        <Grid item xs={12} md={6}>
          <ChartCard
            title="Clientes principales"
            subtitle="Top clientes por valor vendido"
            icon={<GroupsOutlinedIcon />}
          >
            {clientesData.length > 0 ? (
              <ResponsiveContainer width="100%" height="100%">
                <PieChart>
                  <Pie
                    data={clientesData}
                    dataKey="total"
                    nameKey="name"
                    innerRadius={58}
                    outerRadius={105}
                    paddingAngle={3}
                    label={({ name, percent }) => `${String(name).slice(0, 12)} ${((percent || 0) * 100).toFixed(0)}%`}
                  >
                    {clientesData.map((_entry, index) => (
                      <Cell key={index} fill={COLORS[index % COLORS.length]} />
                    ))}
                  </Pie>
                  <Tooltip content={<CustomTooltip />} />
                </PieChart>
              </ResponsiveContainer>
            ) : (
              <EmptyChart text="Sin ventas suficientes para mostrar clientes" />
            )}
          </ChartCard>
        </Grid>

        <Grid item xs={12} md={6}>
          <ChartCard
            title="Historial precio compra"
            subtitle="Evolución del costo promedio registrado"
            icon={<TrendingUpOutlinedIcon />}
          >
            {comprasLinea.length > 0 ? (
              <ResponsiveContainer width="100%" height="100%">
                <LineChart data={comprasLinea}>
                  <CartesianGrid strokeDasharray="3 3" vertical={false} />
                  <XAxis dataKey="fecha" tick={{ fontSize: 11 }} />
                  <YAxis tick={{ fontSize: 12 }} />
                  <Tooltip content={<CustomTooltip />} />
                  <Line
                    type="monotone"
                    dataKey="precio"
                    name="Costo promedio"
                    stroke="#ED6C02"
                    strokeWidth={3}
                    dot={{ r: 3 }}
                    activeDot={{ r: 6 }}
                  />
                </LineChart>
              </ResponsiveContainer>
            ) : (
              <EmptyChart text="Sin historial de compras para graficar" />
            )}
          </ChartCard>
        </Grid>

        <Grid item xs={12} md={6}>
          <ChartCard
            title="Historial precio venta"
            subtitle="Evolución del precio promedio vendido"
            icon={<TrendingUpOutlinedIcon />}
          >
            {ventasLinea.length > 0 ? (
              <ResponsiveContainer width="100%" height="100%">
                <LineChart data={ventasLinea}>
                  <CartesianGrid strokeDasharray="3 3" vertical={false} />
                  <XAxis dataKey="fecha" tick={{ fontSize: 11 }} />
                  <YAxis tick={{ fontSize: 12 }} />
                  <Tooltip content={<CustomTooltip />} />
                  <Line
                    type="monotone"
                    dataKey="precio"
                    name="Precio promedio"
                    stroke="#1565C0"
                    strokeWidth={3}
                    dot={{ r: 3 }}
                    activeDot={{ r: 6 }}
                  />
                </LineChart>
              </ResponsiveContainer>
            ) : (
              <EmptyChart text="Sin historial de ventas para graficar" />
            )}
          </ChartCard>
        </Grid>

        <Grid item xs={12} md={6}>
          <ChartCard
            title="Stock por bodega"
            subtitle="Cantidad equivalente actual y stock mínimo"
            icon={<Inventory2OutlinedIcon />}
          >
            {stockData.length > 0 ? (
              <ResponsiveContainer width="100%" height="100%">
                <BarChart data={stockData}>
                  <CartesianGrid strokeDasharray="3 3" vertical={false} />
                  <XAxis dataKey="name" tick={{ fontSize: 11 }} />
                  <YAxis tick={{ fontSize: 12 }} />
                  <Tooltip content={<CustomTooltip />} />
                  <Bar dataKey="cantidad" name="Cantidad equivalente" fill="#1565C0" radius={[10, 10, 0, 0]} />
                  <Bar dataKey="minimo" name="Mínimo" fill="#D32F2F" radius={[10, 10, 0, 0]} />
                </BarChart>
              </ResponsiveContainer>
            ) : (
              <EmptyChart text="Sin stock por bodega para graficar" />
            )}
          </ChartCard>
        </Grid>

        <Grid item xs={12} md={6}>
          <ChartCard
            title="Actividad registrada"
            subtitle="Distribución de registros conectados al producto"
            icon={<QueryStatsOutlinedIcon />}
          >
            {actividadData.length > 0 ? (
              <Box sx={{ display: "flex", height: "100%", alignItems: "center", gap: 2 }}>
                <Box sx={{ flex: 1, height: "100%", minHeight: 260 }}>
                  <ResponsiveContainer width="100%" height="100%">
                    <PieChart>
                      <Pie
                        data={actividadData}
                        dataKey="value"
                        nameKey="name"
                        innerRadius={58}
                        outerRadius={105}
                        paddingAngle={4}
                        label={({ name, percent }) => `${name} ${((percent || 0) * 100).toFixed(0)}%`}
                      >
                        {actividadData.map((_entry, index) => (
                          <Cell key={index} fill={COLORS[index % COLORS.length]} />
                        ))}
                      </Pie>
                      <Tooltip content={<CustomTooltip />} />
                    </PieChart>
                  </ResponsiveContainer>
                </Box>

                <Stack spacing={1.1} sx={{ minWidth: 145 }}>
                  {actividadData.map((item, index) => (
                    <Box
                      key={item.name}
                      sx={{
                        display: "flex",
                        alignItems: "center",
                        justifyContent: "space-between",
                        gap: 1.5
                      }}
                    >
                      <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
                        <Box
                          sx={{
                            width: 10,
                            height: 10,
                            borderRadius: "50%",
                            bgcolor: COLORS[index % COLORS.length]
                          }}
                        />
                        <Typography sx={{ fontSize: 12.5, color: "#475569", fontWeight: 800 }}>
                          {item.name}
                        </Typography>
                      </Box>

                      <Typography sx={{ fontSize: 13, color: "#0f172a", fontWeight: 900 }}>
                        {item.value}
                      </Typography>
                    </Box>
                  ))}
                </Stack>
              </Box>
            ) : (
              <EmptyChart text="Sin actividad registrada para graficar" />
            )}
          </ChartCard>
        </Grid>
      </Grid>

      <Paper
        sx={{
          mt: 2.5,
          p: 2.5,
          borderRadius: 3,
          border: "1px solid #e2e8f0",
          bgcolor: "#f8fafc"
        }}
      >
        <Stack direction="row" spacing={1} flexWrap="wrap" useFlexGap>
          <Chip
            label={`Compra prom: ${money(resumen.promedioCompra)}`}
            color="warning"
            sx={{ fontWeight: 800, borderRadius: 2 }}
          />
          <Chip
            label={`Venta prom: ${money(resumen.promedioVenta)}`}
            color="info"
            sx={{ fontWeight: 800, borderRadius: 2 }}
          />
          <Chip
            label={`Margen estimado: ${number(resumen.margen)}%`}
            color={resumen.margen >= 0 ? "success" : "error"}
            sx={{ fontWeight: 800, borderRadius: 2 }}
          />
          <Chip
            label={`Notas crédito: ${money(stats.notas_credito_total || 0)}`}
            color="default"
            variant="outlined"
            sx={{ fontWeight: 800, borderRadius: 2 }}
          />
        </Stack>

        <Typography sx={{ color: "#64748b", fontSize: 12.5, mt: 1.5 }}>
          La utilidad es estimada usando el precio promedio de compra multiplicado por la cantidad neta vendida. Después podemos mejorarla usando costeo FIFO/promedio real por movimiento.
        </Typography>
      </Paper>
    </Box>
  );
};

export default ProductChartsTab;